import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, FileSpreadsheet, Users, Mail, FileText, ReceiptText,
  Moon, Sun, LogOut, CheckCircle2, Menu, RefreshCw, Clock, FileDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/date";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link" },
  { to: "/receivables", label: "Receivables", icon: ReceiptText, testid: "nav-receivables-link" },
  { to: "/import", label: "Import", icon: FileSpreadsheet, testid: "nav-import-link" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers-link" },
  { to: "/reports", label: "Reports", icon: FileText, testid: "nav-reports-link" },
  { to: "/paid", label: "Paid", icon: CheckCircle2, testid: "nav-paid-link" },
  { to: "/statements", label: "Statements", icon: FileDown, testid: "nav-statements-link" },
  { to: "/reminders", label: "Reminders", icon: Mail, testid: "nav-reminders-link" },
];

function useSync(onDone) {
  const [syncing, setSyncing] = useState(false);
  const run = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/sync/run");
      if (data.status === "skipped") {
        toast.warning(data.message || "Sync skipped — configure Dropbox links in Import");
        onDone?.();
      } else {
        const parts = Object.entries(data.summary || {}).map(([s, r]) => `${s}: ${r.imported} invoices`);
        toast.success(`Sync complete — ${parts.join(", ") || "no files"}. Refreshing…`);
        if (data.errors && Object.keys(data.errors).length)
          toast.error(`Some files failed: ${Object.keys(data.errors).join(", ")}`);
        // Reload so every page's figures reflect the freshly synced data.
        setTimeout(() => window.location.reload(), 1200);
      }
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };
  return { syncing, run };
}

const fmtDT = (iso) => (iso ? fmtDateTime(iso) : "never");

const STALE_MS = 24 * 60 * 60 * 1000;
const isStale = (iso) => !iso || (Date.now() - new Date(iso).getTime()) > STALE_MS;

function FileTime({ label, iso, testid }) {
  const stale = isStale(iso);
  return (
    <span data-testid={testid} className={stale ? "text-amber-600 dark:text-amber-500" : ""}>
      {label}:{" "}
      <span className={`font-semibold ${stale ? "text-amber-600 dark:text-amber-500" : "text-foreground"}`}>
        {fmtDT(iso)}
      </span>
      {stale && iso && <span className="ml-1" title="Not updated in over 24 hours">⚠</span>}
    </span>
  );
}

export default function Layout({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [status, setStatus] = useState(null);
  const fetchStatus = () => api.get("/sync/status").then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { fetchStatus(); }, []);
  const { syncing, run } = useSync(fetchStatus);
  const [menuOpen, setMenuOpen] = useState(false);

  const navLink = (n, onClick) => {
    const active = loc.pathname === n.to;
    const Icon = n.icon;
    return (
      <Link
        key={n.to}
        to={n.to}
        onClick={onClick}
        data-testid={n.testid}
        className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
          active
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:text-foreground hover:bg-muted"
        }`}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span>{n.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="backdrop-blur-md bg-background/80 sticky top-0 z-40 border-b border-border/60">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-2 lg:gap-4">
            {/* Mobile hamburger */}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden shrink-0" data-testid="mobile-menu-trigger" aria-label="Open menu">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <SheetHeader className="p-4 border-b border-border/60">
                  <SheetTitle className="flex items-center text-left">
                    <img src="/simplex-logo.png" alt="Simplex" className="h-8 w-auto dark:invert" />
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-1 p-3">
                  {NAV.map((n) => navLink(n, () => setMenuOpen(false)))}
                </nav>
              </SheetContent>
            </Sheet>

            <Link to="/" className="flex items-center shrink-0" data-testid="brand-logo">
              <img src="/simplex-logo.png" alt="Simplex Receivables Trackers" className="h-7 lg:h-8 w-auto dark:invert" />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
              {NAV.map((n) => navLink(n))}
            </nav>

            <div className="flex items-center gap-1.5 shrink-0 ml-auto lg:ml-0">
              <Button
                onClick={run}
                disabled={syncing}
                size="sm"
                data-testid="global-sync-button"
                className="gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
                <span className="hidden sm:inline">{syncing ? "Syncing…" : "Sync"}</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggle}
                data-testid="theme-toggle"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={async () => { await logout(); nav("/login"); }}
                data-testid="logout-button"
                aria-label="Logout"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        <div className="border-t border-border/60 bg-muted/30">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-1.5 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground" data-testid="file-updated-bar">
            <span className="inline-flex items-center gap-1 font-medium"><Clock className="w-3 h-3" /> Files last saved</span>
            <FileTime label="Domestic" iso={status?.domestic?.updated_at} testid="domestic-updated" />
            <span className="hidden sm:inline text-border">·</span>
            <FileTime label="Export" iso={status?.export?.updated_at} testid="export-updated" />
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 lg:py-8">{children}</main>
    </div>
  );
}
